import { forwardRef, useContext, useMemo, useRef, useEffect } from 'react';
import type { FC, Ref } from 'react';
import styled, { css } from 'styled-components';

import {
  Button,
  Icon,
  Avatar,
  Count,
  Tooltip,
  useModalManager,
  useI18n,
  useConfiguration,
  useElement,
  formatListToLocaleString,
  Flex,
  defaultThemeProp,
  useUID
} from '@pega/cosmos-react-core';
import type { ForwardProps, ModalMethods } from '@pega/cosmos-react-core';
import BareRoleButton from '@pega/cosmos-react-core/lib/components/Button/BareRoleButton';

import { FeedContext } from './Feed.context';
import FeedButton from './FeedButton';
import FeedModalList from './FeedModalList';
import type { FeedPostBaseProps, FeedModalListProps } from './Feed.types';

const InlineBareButton = styled(BareRoleButton)(({ theme }) => {
  return css`
    position: relative;
    display: inline-block;
    line-height: 1;
    outline: none;
    border-radius: calc(${theme.base['border-radius']} * 9999);

    &:hover {
      &::before {
        content: '';
        position: absolute;
        top: calc(${theme.components.button['border-width']} * -1);
        bottom: calc(${theme.components.button['border-width']} * -1);
        left: calc(${theme.components.button['border-width']} * -1);
        right: calc(${theme.components.button['border-width']} * -1);
        border-radius: inherit;
        background-color: currentColor;
        opacity: 0.15;
      }
    }

    &:focus-visible {
      box-shadow: ${theme.components.button['focus-shadow']};
    }
  `;
});

InlineBareButton.defaultProps = defaultThemeProp;

type FeedLikeButtonProps = Pick<
  FeedPostBaseProps,
  | 'id'
  | 'liked'
  | 'likeCount'
  | 'likes'
  | 'onLikeClick'
  | 'onLikeCountInteraction'
  | 'onLoadMoreLikes'
  | 'likesLoading'
>;

const FeedLikeButton: FC<FeedLikeButtonProps & ForwardProps> = forwardRef(function FeedLikeButton(
  props,
  ref: Ref<HTMLButtonElement>
) {
  const {
    id,
    liked,
    likeCount = 0,
    likes = [],
    onLikeClick,
    onLikeCountInteraction,
    onLoadMoreLikes,
    likesLoading
  } = props;
  const t = useI18n();
  const { locale } = useConfiguration();
  const { create } = useModalManager();
  const { userInfo: loggedInUser } = useContext(FeedContext);
  const [countEl, setCountEl] = useElement(null);
  const likesModalRef = useRef<ModalMethods<FeedModalListProps>>();
  const likeCountId = useUID();

  useEffect(() => {
    likesModalRef.current?.update({
      count: likeCount,
      heading: t('feed_liked_by'),
      listItems: likes,
      onLoadMore: onLoadMoreLikes,
      loading: likesLoading
    });
  }, [likeCount, likes, onLoadMoreLikes, likesLoading]);

  const likesAttributes = useMemo(() => {
    const likesList = likes.map(x => x.primary.toString());
    return {
      ariaLabel: likeCount
        ? `${t('feed_liked_by')} ${formatListToLocaleString(likesList, t, locale, {
            count: likeCount
          })}`
        : t('feed_post_likes', [likeCount], { count: likeCount }),
      label: formatListToLocaleString(likesList, t, locale, {
        count: likeCount,
        separator: '\n'
      })
    };
  }, [likes, likeCount]);

  const onLikeCountClick = () => {
    if (likes.length) {
      likesModalRef.current = create(FeedModalList, {
        count: likeCount,
        heading: t('feed_liked_by'),
        listItems: likes,
        onLoadMore: onLoadMoreLikes,
        loading: likesLoading
      });
    } else {
      onLikeCountInteraction?.();
    }
  };

  return (
    <Flex container={{ gap: 0.5, alignItems: 'center' }}>
      <Button
        variant='simple'
        icon
        compact
        as={FeedButton}
        onClick={() => {
          const user = {
            id: loggedInUser.username,
            primary: loggedInUser.fullname,
            secondary: loggedInUser.meta,
            visual: (
              <Avatar size='m' imageSrc={loggedInUser.avatarSrc} name={loggedInUser.fullname} />
            )
          };
          onLikeClick({
            id,
            user
          });
        }}
        emphasized={liked}
        label={liked ? t('feed_unlike_label') : t('feed_like_label')}
        aria-describedby={likeCount > 0 ? likeCountId : undefined}
        ref={ref}
      >
        <Icon name={liked ? 'thumbs-up-solid' : 'thumbs-up'} />
      </Button>
      {likeCount > 0 && (
        <>
          <InlineBareButton
            ref={setCountEl}
            role={likes.length ? 'button' : undefined}
            onClick={onLikeCountClick}
            onFocus={onLikeCountInteraction}
            onMouseOver={onLikeCountInteraction}
            aria-label={likesAttributes.ariaLabel}
            tabIndex={0}
          >
            <Count
              id={likeCountId}
              aria-label={t('feed_post_likes', [likeCount], { count: likeCount })}
            >
              {likeCount}
            </Count>
          </InlineBareButton>
          <Tooltip target={countEl} showDelay='none' hideDelay='none' describeTarget={false}>
            {likesAttributes.label || t('feed_post_likes', [likeCount], { count: likeCount })}
          </Tooltip>
        </>
      )}
    </Flex>
  );
});

export default FeedLikeButton;
